import { showToast, TOAST } from '@mimir/web/eventBus';
import { getGlobalStore } from '@mimir/web/store';
import type { FilesystemApis } from '@mimir/common/upload/fileOps';
import { createNodeFsApis } from '@mimir/common/nodejs/nodeFileOps';
import { BasicUploadManager, type ManagedUploadItem } from '@mimir/common/upload/BasicUploadManager';
import type { UploadManagerOptions} from '@mimir/common/upload/upload';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import { ConsoleLogger } from '@mimir/common/util/logging';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import {
  trackUploadStart,
  trackUploadComplete,
  trackUploadError,
  trackUploadCancelled,
  trackUploadProgress,
  updateUploadItemId
} from './sentryUploadTracking';
import type { QuickPreview } from '../model/quickPreview';
import { searchState} from '@mimir/web/globals/searchState';
import type { UpdateSearchHitById } from '../model/searchState';


export class BasicUiUploadManager extends BasicUploadManager {
  constructor(protected applicationContext: ApplicationContext, options: UploadManagerOptions) {
    let fsApis: FilesystemApis | undefined = undefined;
    const nodeApi = applicationContext.nodeApi;
    if (nodeApi) {
      fsApis = createNodeFsApis(new ConsoleLogger(), nodeApi, true);
    }
    super(applicationContext, options, fsApis);
  }

  public isUploading(): boolean {
    /*
      Stubbed.

      Used by UploadTable to determine whether to offer the ability to pause and
      resume. Stubbed out at present, since the original code was not reliable
      was only partially implemented.

      TODO: Get rid of pause/resume completely, or implement it reliably using
      generic async pause points, passed in a similar fashion to the
      CancelToken.
    */
    return false;
  }

  public isPaused(): boolean {
    // Stubbed
    return false;
  }

  protected onUploadStart(uploadItem: ManagedUploadItem) {
    // Track upload start with Sentry
    trackUploadStart({
      id: uploadItem.id,
      file: {
        name: uploadItem.file.name,
        size: uploadItem.file.size,
        type: uploadItem.file.type || 'unknown'
      },
      useMultiPart: uploadItem.useMultiPart,
      isExistingItem: uploadItem.isExistingItem
    });

  }

  protected onUpdateUploadItemId(localId: string, uploadItem: ManagedUploadItem) {
    // After beforeUpload, the uploadItem.id will be the real Mimir GUID
    // Update the Sentry tracking context with the real ID
    if (localId !== uploadItem.id && !uploadItem.isExistingItem) {
      updateUploadItemId(localId, uploadItem.id);
    }
  }

  protected onUploadCancelled(itemId: string) {
    // Track cancellation with Sentry
    trackUploadCancelled(itemId, 'User cancelled');
  }

  protected onUploadComplete(itemId: string, fileSize: number) {
    // Track successful completion
    trackUploadComplete(itemId, fileSize);
  }

  protected onUploadError(itemId: string, error: any) {
    // Track upload error
    trackUploadError(itemId, error, 0, 0);
  }

  public async pauseUpload() {
    // Stubbed.
  }

  public async resumeUpload(): Promise<void> {
    // Stubbed.
  }

  protected reportBriefError(message: string) {
    showToast(message, TOAST.ERROR);
  }

  protected deleteItemUploadLock(itemId: string): Promise<void> {
    return getGlobalStore().dispatch('deleteItemUploadLock', itemId);
  }
}

export class MainUploadManager extends BasicUiUploadManager {
  constructor(
    protected applicationContext: ApplicationContext,
    options: UploadManagerOptions,
    private quickPreview: QuickPreview
  ) {
    super(applicationContext, options);
  }

  protected onConclusionChange(payload: { id: string, progressInfo: ProgressInfo}) {
    // Track progress with Sentry
    trackUploadProgress(payload.id, payload.progressInfo);

    const update: UpdateSearchHitById  = {
      id: payload.id,
      update: {
        id: payload.id,
        progressInfo: payload.progressInfo
      }
    };
    const store = getGlobalStore();
    searchState.ops.updateSearchHitById(update);
    store.commit('updateCurrentItem', update);
    this.quickPreview.ops.updateCurrentPreviewItem(update);
  }
}

/**
 *  This upload manager is identical to the MainUploadManager except it does
 *  not mutate the item currently loaded in the QuickPreview when there are
 *  progress updates from the upload manager.
 */
export class SharePageUploadManager extends BasicUiUploadManager {
  constructor(
    protected applicationContext: ApplicationContext,
    options: UploadManagerOptions,
  ) {
    super(applicationContext, options);
  }

  protected onConclusionChange(payload: { id: string, progressInfo: ProgressInfo}) {
    // Track progress with Sentry
    trackUploadProgress(payload.id, payload.progressInfo);

    const update : UpdateSearchHitById = {
      id: payload.id,
      update: {
        id: payload.id,
        progressInfo: payload.progressInfo
      }
    };
    const store = getGlobalStore();
    searchState.ops.updateSearchHitById(update);
    store.commit('updateCurrentItem', update);
  }
}
